/**
 * Type-ahead search implementation for blueprint search
 */

export class Search {
  private input: HTMLInputElement;
  private resultsContainer: HTMLElement;
  private blueprintNames: string[] = [];
  private onSelect: (blueprint: string) => void;
  private debounceTimer: number | null = null;
  private selectedIndex: number = -1;

  constructor(inputId: string, resultsId: string, onSelect: (bp: string) => void) {
    const input = document.getElementById(inputId) as HTMLInputElement;
    const results = document.getElementById(resultsId);

    if (!input || !results) {
      throw new Error(`Search elements not found: ${inputId}, ${resultsId}`);
    }

    this.input = input;
    this.resultsContainer = results;
    this.onSelect = onSelect;

    this.setupEventListeners();
  }

  /**
   * Set the list of searchable blueprint names
   */
  setBlueprints(names: string[]): void {
    this.blueprintNames = names;
    // Enable the input now that we have data
    this.input.disabled = false;
    this.input.placeholder = 'Search blueprints...';
  }

  private setupEventListeners(): void {
    // Input event for search
    this.input.addEventListener('input', () => {
      this.handleInput();
    });

    // Keyboard navigation
    this.input.addEventListener('keydown', (e) => {
      this.handleKeydown(e);
    });

    // Click outside to close results
    document.addEventListener('click', (e) => {
      if (
        !this.input.contains(e.target as Node) &&
        !this.resultsContainer.contains(e.target as Node)
      ) {
        this.hideResults();
      }
    });

    // Focus to show results if there's a query
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim()) {
        this.search(this.input.value);
      }
    });
  }

  private handleInput(): void {
    // Debounce input
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      this.search(this.input.value);
    }, 150);
  }

  private handleKeydown(e: KeyboardEvent): void {
    const items = this.resultsContainer.querySelectorAll('.search-result-item');

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateSelection();
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection();
        break;

      case 'Enter':
        e.preventDefault();
        if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
          const blueprint = items[this.selectedIndex].getAttribute('data-blueprint');
          if (blueprint) {
            this.selectBlueprint(blueprint);
          }
        }
        break;

      case 'Escape':
        this.hideResults();
        this.input.blur();
        break;
    }
  }

  private updateSelection(): void {
    const items = this.resultsContainer.querySelectorAll('.search-result-item');
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (i === this.selectedIndex) {
        item.classList.add('selected');
        item.scrollIntoView({ block: 'nearest' });
      } else {
        item.classList.remove('selected');
      }
    }
  }

  private search(query: string): void {
    const trimmed = query.trim().toLowerCase();

    if (!trimmed) {
      this.hideResults();
      return;
    }

    // Simple substring search (case-insensitive)
    const matches = this.blueprintNames.filter((name) => name.toLowerCase().includes(trimmed));

    // Sort: prefer matches that start with the query
    matches.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      const aStarts = aLower.startsWith(trimmed);
      const bStarts = bLower.startsWith(trimmed);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.localeCompare(b);
    });

    // Limit to top 20
    const topMatches = matches.slice(0, 20);

    this.renderResults(topMatches, trimmed);
  }

  private renderResults(matches: string[], query: string): void {
    this.selectedIndex = -1;

    if (matches.length === 0) {
      this.resultsContainer.innerHTML = '<div class="search-no-results">No matches found</div>';
      this.resultsContainer.classList.add('visible');
      return;
    }

    this.resultsContainer.innerHTML = matches
      .map((name) => {
        // Highlight the matching part
        const highlighted = this.highlightMatch(name, query);
        return `<div class="search-result-item" data-blueprint="${name}">${highlighted}</div>`;
      })
      .join('');

    this.resultsContainer.classList.add('visible');

    // Add click handlers
    this.resultsContainer.querySelectorAll('.search-result-item').forEach((item) => {
      item.addEventListener('click', () => {
        const blueprint = item.getAttribute('data-blueprint');
        if (blueprint) {
          this.selectBlueprint(blueprint);
        }
      });
    });
  }

  private highlightMatch(text: string, query: string): string {
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(query);

    if (index === -1) return text;

    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);

    return `${before}<mark>${match}</mark>${after}`;
  }

  private selectBlueprint(blueprint: string): void {
    this.input.value = blueprint;
    this.hideResults();
    this.onSelect(blueprint);
  }

  private hideResults(): void {
    this.resultsContainer.classList.remove('visible');
    this.resultsContainer.innerHTML = '';
    this.selectedIndex = -1;
  }
}
